/*
    This file is part of Universal Sequencer.
    Universal Sequencer is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequencer is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

// Encapsulates the mapping of keys to triggers (such as sample and slice)
// Handles triggers and stores the mapping

import KeyboardRecordedTrigger from 'keyboard/KeyboardRecordedTrigger.js';
import ComputerKeyboard from 'keyboard/ComputerKeyboard.js';
import RecordedTrigger from 'keyboard/RecordedTrigger.js';
import StepData from 'pattern/StepData.js';
import Notes from 'midi/Notes.js';
import {PADS, KEYBOARD} from 'midi/constants.js';
import {
    setLastMidiKeyMapped,
    setSelectedTrackSlice
} from 'actions/index.js';
import {setKeyMappings} from 'actions/index.js';
import {setPadMappings} from 'actions/index.js';
import {INSTRUMENT, SAMPLER, MIDI, AUDIO} from 'constants/track-types.js';
import {setLastNotePlayed} from 'actions/index.js';

const DEFAULT_STEP = new StepData();

class KeyboardEngine {
    constructor(sequencerEngine, store) {
        this.keyMappings = {};
        this.midiMappings = {};
        this.sequencerEngine = sequencerEngine;
        this.recordedMidiTriggers = {};
        this.computerKeyboard = new ComputerKeyboard();
        this.store = sequencerEngine.store;
    }

    get midi() {
        return this.sequencerEngine.midi;
    }

    hasKeyMapping(key) {
        return key in this.keyMappings;
    }

    deleteKeyMapping(key) {
        delete this.keyMappings[key];
        this.sequencerEngine.store.dispatch(setKeyMappings(this.keyMappings));
    }

    deleteMidiMapping(key) {
        delete this.padMappings[key];
        let input = Object.keys(this.midiMappings)[0];
        this.sequencerEngine.store.dispatch(
            setPadMappings(this.midiMappings[input]));
    }

    // deletes all mappings (key and pad) for that trackNumber
    // and updates the other mappings to have correct mappings
    deleteTrack(trackNumber) {
        this.keyMappings = this.deleteHelper(this.keyMappings, trackNumber);
        this.midiMappings = this.deleteMidiHelper(this.midiMappings, trackNumber);

        this.sequencerEngine.store.dispatch(setKeyMappings(this.keyMappings));
        let key = Object.keys(this.midiMappings)[0];
        this.sequencerEngine.store.dispatch(setPadMappings(this.midiMappings[key]));
    }

    deleteMidiHelper(map, trackNumber) {
        let x = {};
        for (let i in map) {
            x[i] = this.deleteHelper(map[i], trackNumber);
        }
        return x;
    }

    deleteHelper(map, trackNumber) {
        let newMap = {};
        for (let key in map) {
            let value = map[key];
            if (trackNumber >= value.trackNumber) {
                newMap[key] = value;
            } else if (trackNumber < value.trackNumber) {
                value.trackNumber--;
                newMap[key] = value;
            }
        }
        return newMap;
    }

    hasMidiMappings() {
        return Object.keys(this.midiMappings).length > 0;
    }

    // called from StepSequencer when we have an alphabet "key down"
    // event. This function either tries to play samples or a midi
    // device
    onKeyDown(key, resolution, trigger=true, isMidi, inputName, velocity) {
        key = key.toLowerCase();
        this.handleOctaves(key);
        let note = isMidi ? Notes.fromString(key).getTranspose() :
            this.computerKeyboard.getTransposeForKey(key);

        let normalizedNote = note % 12;
        if (normalizedNote < 0) {
            normalizedNote = 12 + normalizedNote;
        }

        this.store.dispatch(setLastNotePlayed(note));
        this.onNoteDown(key, note, resolution, trigger, inputName, isMidi, velocity);
    }

    onNoteDown(key, note, resolution, trigger=true, inputName, isMidi, velocity) {
        if (this.sequencerEngine.tracks.length === 0) {
            return;
        }
        
        let toggledTracks = this.getToggledTracks(inputName, isMidi);
        
        if (toggledTracks.length > 0) {
            // Deals with "enabled" tracks, meaning user is using a
            // computer keyboard/midi keyboard to play the tracks like
            // a keyboard (i.e. triggering different pitches)
            if (note === undefined) {
                return;
            }

            // sampler or audio tracks
            toggledTracks
                .filter(track => !track.rolling)
                .forEach(
                track => {
                    let stepData = DEFAULT_STEP; 
                    stepData.copyFrom(track.trackData);
                    stepData.slice = 0;
                    stepData.duration = undefined;
                    stepData.transpose = note + track.trackData.transpose;
                    stepData.velocity = velocity === null ? track.trackData.velocity : velocity;
                    if (track.instrument) {
                        stepData.release = null;
                    }
                    track.midiEffects.liveInputTrigger(stepData);
                });
        } else if (key in this.keyMappings) {
            // treat keyboard as a drum pad
            this.keyMappings[key].trigger(this.sequencerEngine, resolution);
        } else if (note in this.keyMappings) {
            this.keyMappings[note].trigger(this.sequencerEngine, resolution);
        } else if (inputName in this.midiMappings && key.toUpperCase() in this.midiMappings[inputName]) {
            this.midiMappings[inputName][key.toUpperCase()].trigger(this.sequencerEngine, resolution, velocity, true);
        } else {
            let track = this.sequencerEngine.tracks[this.sequencerEngine.cursor.track];
            let selectedSlice = this.getSelectedSlice(track);
            if (inputName === undefined) {
                // then map key
                if (!this.alreadyMapped(track, selectedSlice)) {
                    this.map(key, this.sequencerEngine.cursor.track, this.getSelectedSlice(track));
                    this.onNoteDown(key, note, resolution, trigger, inputName, isMidi, velocity);
                }
            } else {
                if (!this.alreadyMidiMapped(inputName, track, selectedSlice)) {
                    this.midiMap(inputName, key, this.sequencerEngine.cursor.track, selectedSlice);
                    this.onNoteDown(key, note, resolution, trigger, inputName, isMidi, velocity);
                }
            }
        }
    }

    handleOctaves(key) {
        if (key === 'z') {
            this.computerKeyboard.downOctave();
        } else if (key === 'x') {
            this.computerKeyboard.upOctave();
        }
    }

    getToggledTracks(inputName, isMidi) {
        let toggledTracks = this.sequencerEngine.subSequences
            .flatMap(x => x.tracks)
            .filter(
            track =>
                track.isEnabled);
        toggledTracks = toggledTracks
            .filter(
                track => {
                    let midiInputs = track.midiInputs;
                    return midiInputs.accepts(isMidi, inputName);
                });

        return toggledTracks;
    }

 
    recordHit(key, isMidi, resolution, quantized, inputName, velocity) {
        let toggledTracks = this.getToggledTracks(inputName, isMidi);
        if (toggledTracks.length > 0) {
            // we're recording a keyboard (either computer keyboard
            // or midi keyboard) playing note 
            let note = isMidi ? Notes.fromString(key).getTranspose() : this.computerKeyboard.getTransposeForKey(key);
            if (note !== undefined) {
                // make note of the toggled tracks (as they are the ones
                // we wish to record)
                return new KeyboardRecordedTrigger(
                    note,
                    toggledTracks,
                    resolution,
                    this.sequencerEngine,
                    quantized,
                    velocity);
            } 
        } else if (key in this.keyMappings) {
            var mapping = this.keyMappings[key];
            return new RecordedTrigger(
                mapping.track,
                mapping.sliceNumber,
                resolution,
                this.sequencerEngine,
                quantized);
        } else if (inputName in this.midiMappings && key in this.midiMappings[inputName]) {
            var mapping = this.midiMappings[inputName][key];
            return new RecordedTrigger(
                mapping.track,
                mapping.sliceNumber,
                resolution,
                this.sequencerEngine,
                quantized,
                velocity);
        } else {
            return null;
        }
    }

    getRecordedMidiTrigger(midiMapping, resolution, quantized) {
        return new RecordedTrigger(
            midiMapping.track,
            midiMapping.sliceNumber,
            resolution,
            this.sequencerEngine,
            quantized);
    }

    get padMappings() {
        let keys = Object.keys(this.midiMappings);
        if (keys.length === 0) {
            return {};
        }
        return this.midiMappings[keys[0]];
    }

    isMidiMapped(key) {
        return this.padMappings[key] !== undefined;
    }

    alreadyMidiMapped(inputName, track, sliceNumber) {
        if (this.midiMappings[inputName] === undefined) {
            return false;
        }
        for (let key in this.midiMappings[inputName]) {
            let keyMapping = this.midiMappings[inputName][key];
            if (keyMapping.track  === track &&
                keyMapping.sliceNumber === sliceNumber) {
                return true;
            }
        }
        return false;
    }

    getSelectedSlice(track) {
        return this.sequencerEngine.state.tracks.selectedTrackSlice[track.id] || 0;
    }

    alreadyMapped(track, sliceNumber) {
        for (let key in this.keyMappings) {
            let keyMapping = this.keyMappings[key];
            if (keyMapping.track === track &&
                keyMapping.sliceNumber === sliceNumber) {
                return true;
            }
        }

        return false;
    }

    map(key, trackNumber, sliceNumber=0) {
        let track = this.sequencerEngine.tracks[trackNumber];
        if (track=== undefined) {
            return;
        }
        this.keyMappings[key] = new KeyMapping(key, track, sliceNumber);
        this.sequencerEngine.store.dispatch(setKeyMappings(this.keyMappings));
    }

    midiMap(inputName, note, trackNumber, sliceNumber) {
        let track = this.sequencerEngine.tracks[trackNumber];
        if (track=== undefined) {
            return;
        }
        note = note.toUpperCase();
        let keyMapping = new KeyMapping(note, track, sliceNumber);
        if (!(inputName in this.midiMappings)) {
            this.midiMappings[inputName] = {};
        }
        this.midiMappings[inputName][note] = keyMapping;

        // create simplified mapping to put into redux...
        let noteToMapping = {};
        for (let input in this.midiMappings) {
            let inputMappings = this.midiMappings[input];
            for (let note in inputMappings) {
                noteToMapping[note] = inputMappings[note];
            }
        }
        // can only midi map pads for now...
        this.store.dispatch(setPadMappings(noteToMapping));
        this.store.dispatch(setLastMidiKeyMapped(note));
    }

    getMappings() {
        return this.keyMappings;
    }

    getMidiMappings() {
        return this.midiMappings;
    }

    deleteMappingsForTrack(trackNumber) {
        for (var key in this.keyMappings) {
            if (this.keyMappings[key].trackNumber === trackNumber) {
                delete this.keyMappings[key];
            }
        }

        for (var input in this.midiMappings) {
            let mappings = this.midiMappings[input];
            for (var note in mappings) {
                if (mappings[note].trackNumber == trackNumber) {
                    delete mappings[note];
                }
            }
        }
    }
}

class KeyMapping {
    constructor(key, track, sliceNumber) {
        // todo: make this generic so anything can be mapped to a key
        this.key = key;
        this.track = track;
        this.trackNumber = track.trackNumber;
        this.sliceNumber = sliceNumber;
    } 

    trigger(sequencerEngine, resolution, velocity=null, isMidi) {
        let track = this.track;
        if (!track.rolling) {
            let stepData = DEFAULT_STEP;
            stepData.copyFrom(track.trackData);
            stepData.slice = this.sliceNumber;
            stepData.velocity = velocity;
            stepData.duration = undefined;
            track.midiEffects.liveInputTrigger(stepData);
        }

        sequencerEngine.selectMidiTrack(this.trackNumber);
        sequencerEngine.dispatch(setSelectedTrackSlice(track.id, this.sliceNumber));
    }
}

export default KeyboardEngine;
